import {
  APPLY_FAILURE_BULK_MERGE_FAILED,
  applyFailureReason,
  type ApplyFailureReason,
} from "@common/constants/appliedActions";

export class ApplyHeldBack extends Error {
  readonly heldBackReason: string;

  constructor(heldBackReason: string) {
    super(heldBackReason);
    this.name = "ApplyHeldBack";
    this.heldBackReason = heldBackReason;
  }
}

export interface ApplyOutcomeHandlers {
  markApplied: () => void;
  markFailed: (reason: ApplyFailureReason) => void;
  markPartlyApplied?: (partlyApplied: Error) => void;
  onError: (error: Error) => void;
  onHeldBack?: (heldBack: ApplyHeldBack) => void;
}

export interface BulkOutcomeHandlers {
  markApplied: (actionId: string) => void;
  markFailed: (actionId: string, reason: ApplyFailureReason) => void;
}

export const everyWriteLandedIn = (error: unknown): boolean =>
  (error as { everyWriteLanded?: unknown } | null)?.everyWriteLanded === true;

export const isPartlyAppliedError = (error: unknown): error is Error =>
  error instanceof Error && typeof (error as { landed?: unknown }).landed === "number" && typeof (error as { total?: unknown }).total === "number";

export async function runRecordedApply(
  apply: () => Promise<void>,
  handlers: ApplyOutcomeHandlers,
): Promise<void> {
  try {
    await apply();
  } catch (error) {
    if (error instanceof ApplyHeldBack && handlers.onHeldBack !== undefined) {
      handlers.onHeldBack(error);
      return;
    }
    if (everyWriteLandedIn(error)) {
      handlers.markApplied();
      handlers.onError(error instanceof Error ? error : new Error(String(error)));
      return;
    }
    if (handlers.markPartlyApplied !== undefined && isPartlyAppliedError(error)) {
      handlers.markPartlyApplied(error);
      handlers.onError(error);
      return;
    }
    handlers.markFailed(applyFailureReason(error));
    handlers.onError(error instanceof Error ? error : new Error(String(error)));
    return;
  }
  handlers.markApplied();
}

export function recordBulkApplyOutcomes(
  attemptedActionIds: string[],
  appliedActionIds: Set<string>,
  handlers: BulkOutcomeHandlers,
  failureReasonByActionId: ReadonlyMap<string, ApplyFailureReason> = new Map(),
): void {
  for (const actionId of attemptedActionIds) {
    if (appliedActionIds.has(actionId)) {
      handlers.markApplied(actionId);
    } else {
      handlers.markFailed(actionId, failureReasonByActionId.get(actionId) ?? APPLY_FAILURE_BULK_MERGE_FAILED);
    }
  }
}
